'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { COUNTRIES, findCountry, type Country } from '@/data/countries'

interface Props {
  value: string | null
  onChange: (code: string | null) => void
  required?: boolean
  placeholder?: string
  className?: string
}

/**
 * Searchable Country Combobox (keine externe Library).
 * Nutzbar im Register-Modal und in Settings.
 */
export function CountryCombobox({ value, onChange, required, placeholder, className }: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const selected = findCountry(value)

  const filtered: Country[] = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return COUNTRIES
    return COUNTRIES.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.code.toLowerCase().includes(q)
    )
  }, [query])

  // Klick ausserhalb → schliessen
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Beim Oeffnen: Input fokussieren
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus()
    }
  }, [open])

  const handleSelect = (c: Country) => {
    onChange(c.code)
    setOpen(false)
    setQuery('')
  }

  return (
    <div ref={wrapRef} className={`relative ${className || ''}`}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="input w-full flex items-center justify-between cursor-pointer text-left"
        aria-required={required}
      >
        {selected ? (
          <span className="flex items-center gap-2">
            <span className="text-base">{selected.flag}</span>
            <span>{selected.name}</span>
            <span className="text-[10px] text-[var(--muted)] font-bold">{selected.code}</span>
          </span>
        ) : (
          <span className="text-[var(--muted)]">{placeholder || 'Land auswählen...'}</span>
        )}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-[var(--muted)]">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-[2000] top-[calc(100%+4px)] left-0 right-0 rounded-[10px] border border-[var(--border)] bg-[var(--bg-card)] shadow-xl overflow-hidden">
          <div className="p-2 border-b border-[var(--border)]">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Suchen..."
              className="input w-full !py-1.5 !text-[12px]"
            />
          </div>
          <div className="max-h-[260px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-center text-[11px] text-[var(--muted)]">
                Kein Land gefunden
              </div>
            ) : filtered.map((c, idx) => {
              const isPrioritySeparator = c.priority && idx === 0
              const prevNonPriority = idx > 0 && !filtered[idx - 1].priority
              const showPriorityDivider =
                c.priority && idx > 0 && !filtered[idx - 1].priority === false && filtered[idx - 1].priority
                  ? false
                  : (!c.priority && idx > 0 && filtered[idx - 1].priority)
              return (
                <div key={c.code}>
                  {showPriorityDivider && (
                    <div className="border-t border-[var(--border)]/60 my-1" />
                  )}
                  <button
                    type="button"
                    onClick={() => handleSelect(c)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-[12px] hover:bg-[var(--accent)]/10 transition-colors ${
                      value === c.code ? 'bg-[var(--accent)]/10 text-[var(--accent)]' : 'text-[var(--text)]'
                    }`}
                  >
                    <span className="text-base">{c.flag}</span>
                    <span className="flex-1 font-medium">{c.name}</span>
                    <span className="text-[10px] text-[var(--muted)] font-bold">{c.code}</span>
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
